import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { Get_LeadIndicatorFunnel, Get_TargetFunnel, Get_KiiKpiRelationDetailsById, Add_Update_KiiKpiRelation, Get_KiiKpiRelationDetails } from "../../../../../services/userAPI";
import "./leadIndicatorSelection.css";
import { Card, Checkbox, Select, InputNumber } from "antd";
import toggleImage03 from "../../../../../Images/toggles-fill-image03.png";
import { message } from "antd";
import DeleteIcon from "../../../../../Images/deleteButton-icon.png";
import BackIcon from "../../../../../Images/backIcon.png";
import HomeIcon from "../../../../../Images/homeIcon.png";

const LeadIndicatorSelection = ({ onBack, onSaveSuccess, roleId, targetFunnelName }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const fullState = location.state || {};

    // Fetch lead indicators
    const { data: leadIndicatorData } = useQuery(Get_LeadIndicatorFunnel, {
        variables: { roleId },
    });

    // Fetch target funnel data
    const { data: targetFunnelData } = useQuery(Get_TargetFunnel, {
        variables: { roleId },
    });

    const { data: relationData, refetch } = useQuery(Get_KiiKpiRelationDetails, {
        variables: { roleId },
        skip: !roleId,
        onError: (error) => {
            console.error("GraphQL Error:", error);
        },
    });

    const [deleteKpiKiiRelation] = useMutation(Get_KiiKpiRelationDetailsById);
    const [addUpdateKiiKpiRelation, { loading: saveLoading }] = useMutation(Add_Update_KiiKpiRelation);
    const [selectedIndicators, setSelectedIndicators] = useState({});
    const [weightages, setWeightages] = useState({});
    const [dropdownOpen, setDropdownOpen] = useState({});
    const [errors, setErrors] = useState({});
    const [fetchedRelations, setFetchedRelations] = useState([]);

    const name = location.state?.name;
    const accountName = location.state?.accountName;

    // Process relation data when it's fetched
    useEffect(() => {
        if (relationData?.getKiiKpiRelationDetails) {
            setFetchedRelations(relationData.getKiiKpiRelationDetails);

            // Initialize weightages from fetched data
            const initialWeightages = {};
            relationData.getKiiKpiRelationDetails.forEach((item) => {
                if (item.kiiId && item.id) {
                    initialWeightages[`${item.kpiId}-${item.kiiId}-${item.id}`] = item.relationKpiKii?.toString() || "";
                }
            });
            setWeightages((prev) => ({ ...prev, ...initialWeightages }));
        }
    }, [relationData]);

    // Helper function to group relations by KPI ID
    const getRelationsByKpiId = (kpiId) => {
        return fetchedRelations.filter((relation) => relation.kpiId === kpiId);
    };

    // Combine fetched relations and selected indicators for display
    const getIndicatorsForDisplay = (funnelId) => {
        const relations = getRelationsByKpiId(funnelId);
        const selected = selectedIndicators[funnelId] || [];

        // Map fetched relations
        const relationIndicators = relations.map((relation) => ({
            id: relation.id,
            kiiId: relation.kiiId,
            name: relation.kiiName,
            weightageKey: `${funnelId}-${relation.kiiId}-${relation.id}`,
            isFetched: true,
        }));

        // Map selected indicators (not in fetched relations)
        const selectedIndicatorsData = selected
            .filter((name) => !relations.some((rel) => rel.kiiName?.trim().toLowerCase() === name.trim().toLowerCase()))
            .map((name) => {
                const indicator = leadIndicatorData?.getKiisForRole.find(
                    (i) => i.name?.trim().toLowerCase() === name.trim().toLowerCase()
                );
                return {
                    id: null, // No relation ID yet (newly selected)
                    kiiId: indicator?.id,
                    name,
                    weightageKey: `${funnelId}-${name}`,
                    isFetched: false,
                };
            });

        return [...relationIndicators, ...selectedIndicatorsData];
    };

    const handleIndicatorChange = (funnelId, checkedValues) => {
        setSelectedIndicators((prev) => ({
            ...prev,
            [funnelId]: checkedValues,
        }));

        // Remove error message when a value is selected
        if (checkedValues.length > 0 || getRelationsByKpiId(funnelId).length > 0) {
            setErrors((prev) => ({ ...prev, [funnelId]: "" }));
        }

        // Set default weightages for newly selected indicators
        checkedValues.forEach((value) => {
            const key = `${funnelId}-${value}`;
            if (!weightages[key]) {
                setWeightages((prev) => ({
                    ...prev,
                    [key]: "",
                }));
            }
        });

        // Clear weightages for deselected indicators
        const previousIndicators = selectedIndicators[funnelId] || [];
        const removedIndicators = previousIndicators.filter((indicator) => !checkedValues.includes(indicator));
        removedIndicators.forEach((indicator) => {
            const key = `${funnelId}-${indicator}`;
            setWeightages((prev) => {
                const newWeightages = { ...prev };
                delete newWeightages[key];
                return newWeightages;
            });
        });
    };

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const accountId = location.state?.accountId;
    // const roleId = location.state?.roleId;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    // const name = location.state?.name;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;

    const handleSubmit = async () => {
        let hasError = false;
        let newErrors = {};

        // Check if at least one indicator is selected per funnel
        targetFunnelData?.getKpisForRole.forEach((funnel) => {
            if (
                (!selectedIndicators[funnel.id] || selectedIndicators[funnel.id].length === 0) &&
                getRelationsByKpiId(funnel.id).length === 0
            ) {
                newErrors[funnel.id] = `Please select at least one lead indicator for ${funnel.name}.`;
                hasError = true;
            }
        });

        setErrors(newErrors);

        if (!hasError) {
            // Prepare data for mutation
            const mutationPromises = [];

            // Process newly selected indicators
            targetFunnelData?.getKpisForRole.forEach((funnel) => {
                selectedIndicators[funnel.id]?.forEach((indicatorName) => {
                    const indicator = leadIndicatorData?.getKiisForRole.find(
                        (i) => i.name?.trim().toLowerCase() === indicatorName.trim().toLowerCase()
                    );

                    if (indicator) {
                        const weightage = weightages[`${funnel.id}-${indicatorName}`] || "";
                        mutationPromises.push(
                            addUpdateKiiKpiRelation({
                                variables: {
                                    data: [
                                        {
                                            kpiId: funnel.id,
                                            kiiId: indicator.id,
                                            relationKpiKii: weightage ? parseFloat(weightage) : 1,
                                            kpiKiiRelationId: null, // For new relations
                                        },
                                    ],
                                },
                            })
                        );
                    }
                });
            });

            // Update existing relations with new weightages
            fetchedRelations.forEach((relation) => {
                const weightageKey = `${relation.kpiId}-${relation.kiiId}-${relation.id}`;
                const newWeightage = weightages[weightageKey];

                if (newWeightage && parseFloat(newWeightage) !== relation.relationKpiKii) {
                    mutationPromises.push(
                        addUpdateKiiKpiRelation({
                            variables: {
                                data: [
                                    {
                                        kpiId: relation.kpiId,
                                        kiiId: relation.kiiId,
                                        relationKpiKii: parseFloat(newWeightage),
                                        kpiKiiRelationId: relation.id, // For updating existing relations
                                    },
                                ],
                            },
                        })
                    );
                }
            });

            try {
                await Promise.all(mutationPromises);
                await refetch();

                setSelectedIndicators({});
                message.success("Correlation Configuration is successful!");
                if (onSaveSuccess) {
                    onSaveSuccess();
                } else {
                    navigate("/products-Configuration", { state: fullState });
                }
            } catch (error) {
                console.error("Error updating indicators:", error);
                message.error("An error occurred while saving.");
            }
        }
    };

    // Helper function to delete a fetched relation
    const handleDeleteRelation = async (relation) => {
        try {
            const { data } = await deleteKpiKiiRelation({
                variables: {
                    kpiKiiRelation: relation.id,
                },
            });

            if (data?.deleteKpiKiiRelation?.success) {
                message.success("Indicator deleted successfully!");
                await refetch();
            } else {
                message.error("Failed to delete indicator.");
            }
        } catch (error) {
            console.error("Error deleting relation:", error);
            message.error("An error occurred while deleting.");
        }
    };

    // Helper function to remove a selected (non-fetched) indicator
    const handleRemoveSelectedIndicator = (funnelId, indicatorName) => {
        const newSelected = selectedIndicators[funnelId].filter((name) => name !== indicatorName);
        handleIndicatorChange(funnelId, newSelected);
    };

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate(-1);
        }
    };
    const handleDiscardClick = () => {
        navigate("/admin");
    };

    const handleBackToViewRole = () => {
        const safeRoleId = roleId || location.state?.roleId;
        const safeAccountName = accountName || location.state?.accountName;

        navigate('/view-role', {
            state: {
                roleId: safeRoleId,
                accountId: accountId || location.state?.accountId,
                accountName: safeAccountName,
                name: name || location.state?.name,
                dartsNeeded: dartsNeeded || location.state?.dartsNeeded,
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded,
                dartsFormNeeded: dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution:
                    mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };

    return (
        <div className="lead-indicator-selection-page">
            <div className="lead-indicator-selection-actions">
                <div className="flex items-center justify-between w-full px-2">

                    {/* Back */}
                    <button
                        onClick={handleBack}
                        className="
        flex items-center gap-2
        bg-[#C21A17]
        text-white
        px-5 py-2
        rounded-lg
        text-sm
        font-semibold
        shadow-md
        hover:bg-[#A91513]
        transition-all
        duration-200
    "
                    >
                        <img
                            src={BackIcon}
                            alt="Back"
                            className="w-[15px] h-[15px] object-contain"
                        />
                        Back
                    </button>

                    {/* Home */}
                    <button
                        onClick={() => navigate("/admin")}
                        className="
                            flex items-center gap-2
                            bg-[#C21A17]
                            text-white
                            px-5 py-2
                            rounded-lg
                            text-sm
                            font-semibold
                            shadow-md
                            hover:bg-[#A91513]
                            transition-all
                            duration-200
                          "
                    >
                        <img
                            src={HomeIcon}
                            alt="Home"
                            className="w-[15px] h-[15px] object-contain"
                        />
                        Home
                    </button>

                </div>
            </div>
            <nav className="lead-indicator-selection-breadcrumb" aria-label="Breadcrumb">
                <button type="button" onClick={() => navigate("/admin")}>
                    {accountName || "Account"}
                </button>
                <span aria-hidden="true">›</span>
                <button type="button" onClick={() => navigate("/view-role", { state: fullState })}>
                    {name || "Role"}
                </button>
                <span aria-hidden="true">›</span>
                <button type="button" onClick={() => navigate("/productsConfiguration", { state: fullState })}>
                    Product Configuration
                </button>
                <span aria-hidden="true">›</span>
                <span>Clan</span>
                <span aria-hidden="true">›</span>
                <span aria-current="page">Correlation &amp; Weightage Configuration</span>
            </nav>
            <img src={toggleImage03} className="centered-image" alt="Toggle Image" />
            <div
                className="lead-header-container"
                style={{ marginTop: "20px", marginBottom: "18px" }}
            >
                {/* <h2>Correlation & Weightage Configuration</h2> */}
            </div>

            {targetFunnelData?.getKpisForRole ? (
                targetFunnelData.getKpisForRole.map((funnel) => {
                    const indicatorsForDisplay = getIndicatorsForDisplay(funnel.id);

                    return (
                        <Card key={funnel.id} className="lead-card" style={{ borderRadius: "0px" }}>
                            <div className="lead-dropdown-container">
                                <span className="left-text">{funnel.name}</span>
                                <Select
                                    mode="multiple"
                                    placeholder="Select Lead Indicators"
                                    className="lead-dropdown"
                                    style={{ width: "300px" }}
                                    value={[]}
                                    showArrow={true}
                                    onChange={(values) => handleIndicatorChange(funnel.id, values)}
                                    open={dropdownOpen[funnel.id]}
                                    onDropdownVisibleChange={(open) => setDropdownOpen({ ...dropdownOpen, [funnel.id]: open })}
                                    dropdownRender={(menu) => (
                                        <div>
                                            {leadIndicatorData?.getKiisForRole
                                                ?.filter((indicator) => {
                                                    const indicatorName = indicator?.name?.trim().toLowerCase();
                                                    if (!indicatorName) return false;

                                                    const inFetchedRelations = getRelationsByKpiId(funnel.id)
                                                        .map((relation) => relation.kiiName?.trim().toLowerCase())
                                                        .filter((name) => name != null);

                                                    return !inFetchedRelations.includes(indicatorName);
                                                })
                                                ?.map((indicator) => (
                                                    <div
                                                        key={indicator.id}
                                                        style={{
                                                            padding: "8px 12px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() => {
                                                            const currentSelected = selectedIndicators[funnel.id] || [];
                                                            const indicatorName = indicator.name;
                                                            const isSelected = currentSelected.includes(indicatorName);
                                                            const newSelected = isSelected
                                                                ? currentSelected.filter((name) => name !== indicatorName)
                                                                : [...currentSelected, indicatorName];
                                                            handleIndicatorChange(funnel.id, newSelected);
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={(selectedIndicators[funnel.id] || []).includes(indicator.name)}
                                                            style={{ marginRight: 8 }}
                                                        />
                                                        <span>{indicator.name}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                />
                            </div>

                            {errors[funnel.id] && <p className="error-message">{errors[funnel.id]}</p>}

                            <div className="lead-header">
                                <span>Lead Indicators</span>
                            </div>

                            {/* Display indicators (fetched and selected) */}
                            {indicatorsForDisplay.length === 0 ? (
                                <p className="no-indicators">No Lead Indicators Selected</p>
                            ) : (
                                <div className="fetched-relations">
                                    {indicatorsForDisplay.map((indicator) => (
                                        <div key={indicator.weightageKey} className="indicator-row">
                                            <span>{indicator.name}</span>
                                            {indicator.isFetched ? (
                                                <img
                                                    src={DeleteIcon}
                                                    onClick={() => handleDeleteRelation({ id: indicator.id, kpiId: funnel.id, kiiId: indicator.kiiId })}
                                                    className="lead-delete-btn"
                                                    alt="Delete"
                                                    style={{ cursor: "pointer" }}
                                                />
                                            ) : (
                                                <img
                                                    src={DeleteIcon}
                                                    onClick={() => handleRemoveSelectedIndicator(funnel.id, indicator.name)}
                                                    className="lead-delete-btn"
                                                    alt="Delete"
                                                    style={{ cursor: "pointer" }}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    );
                })
            ) : (
                <p>No target funnels found.</p>
            )}

            <div className="actions">
                {/* <button onClick={onBack} className="back-btn">
                    Back
                </button> */}
                <button
                    onClick={handleSubmit}
                    disabled={saveLoading}
                    className="w-[75px] h-[35px] rounded-[6px] border-0 bg-[#AD0101] text-[14px] font-medium text-white"                >
                    {saveLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white !rounded-small animate-spin"></span> : "Save"}
                </button>
            </div>
        </div>
    );
};

export default LeadIndicatorSelection;